/**
 * Search Mappers
 * Functions to convert products and categories to unified SearchableItem format
 */

import type { MergedCategory } from '@/features/product-library/hooks/useMergedCategories';
import type { ProductLibraryItem } from '@/features/product-library/types';
import type { SearchableItem, SearchableDomain, PlacementType, SearchableItemIcon } from './types';

/**
 * Generate abbreviation from a name
 */
function generateAbbreviation(name: string): string {
  const words = name
    .toUpperCase()
    .trim()
    .split(/[\s_-]+/);
  if (words.length === 1) {
    return words[0].substring(0, 3);
  }
  return words
    .slice(0, 4)
    .map(w => w[0])
    .join('');
}

/**
 * Map domain to placement type
 */
function domainToPlacementType(domain: SearchableDomain): PlacementType {
  switch (domain) {
    case 'lighting_fixtures':
      return 'fixture';
    case 'lighting_controls':
      return 'control';
    case 'lv_products':
      return 'product';
    default:
      return 'product';
  }
}

/**
 * Map product category string to domain
 */
function categoryToDomain(category: string): SearchableDomain {
  const lowerCategory = category.toLowerCase();
  if (lowerCategory === 'fixture' || lowerCategory === 'lighting') {
    return 'lighting_fixtures';
  }
  if (lowerCategory === 'control' || lowerCategory === 'controls') {
    return 'lighting_controls';
  }
  return 'lv_products';
}

/**
 * Map product category to placement type
 */
function categoryToPlacementType(category: string): PlacementType {
  const lowerCategory = category.toLowerCase();
  if (lowerCategory === 'fixture' || lowerCategory === 'lighting') {
    return 'fixture';
  }
  if (lowerCategory === 'control' || lowerCategory === 'controls') {
    return 'control';
  }
  return 'product';
}

/**
 * Map a MergedCategory (from useMergedCategories) to SearchableItem
 */
export function mapMergedCategoryToSearchable(category: MergedCategory): SearchableItem {
  const icon: SearchableItemIcon = {
    type: category.icon_type,
    svg: category.icon_svg || undefined,
    abbreviation: category.abbreviation,
    color: category.fill_color,
    shape: category.icon_shape,
  };

  return {
    id: `category:${category.domain}:${category.category}:${category.subcategory || ''}`,
    kind: 'category',
    domain: category.domain as SearchableDomain,
    placementType: domainToPlacementType(category.domain as SearchableDomain),
    category: category.category,
    subcategory: category.subcategory || undefined,
    displayName: category.display_name,
    icon,
    sourceTable: category.is_system ? 'category_templates' : 'organization_categories',
  };
}

/**
 * Map a ProductLibraryItem to SearchableItem
 */
export function mapProductToSearchable(product: ProductLibraryItem): SearchableItem {
  const icon: SearchableItemIcon = product.image_url
    ? { type: 'url', url: product.image_url }
    : {
        type: 'abbreviation',
        abbreviation: generateAbbreviation(product.brand || product.model || 'P'),
        color: '#6B7280',
      };

  return {
    id: `product:${product.id}`,
    kind: 'product',
    domain: categoryToDomain(product.category),
    placementType: categoryToPlacementType(product.category),
    category: product.category,
    subcategory: product.subcategory,
    displayName: `${product.brand} ${product.model}`,
    secondaryText: `${product.brand} / ${product.category}${product.subcategory ? ' - ' + product.subcategory : ''}`,
    icon,
    productLibraryId: product.id,
    brand: product.brand,
    model: product.model,
    sourceTable: 'product_library',
  };
}

/**
 * Map a category template (from categoryTemplateService) to SearchableItem
 */
export function mapCategoryTemplateToSearchable(
  template: {
    id: string;
    domain: string;
    category: string;
    subcategory?: string | null;
    display_name: string;
    icon_svg?: string | null;
    default_color?: string;
  },
  placementType: PlacementType,
): SearchableItem {
  const icon: SearchableItemIcon = template.icon_svg
    ? { type: 'svg', svg: template.icon_svg, color: template.default_color }
    : {
        type: 'abbreviation',
        abbreviation: generateAbbreviation(template.display_name || template.category),
        color: template.default_color || '#6B7280',
      };

  return {
    id: `category:${template.domain}:${template.category}:${template.subcategory || ''}`,
    kind: 'category',
    domain: template.domain as SearchableDomain,
    placementType,
    category: template.category,
    subcategory: template.subcategory || undefined,
    displayName: template.display_name,
    icon,
    sourceTable: 'category_templates',
  };
}
